import { Location, AsyncPipe } from '@angular/common';
import { Component, OnInit, inject, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, of, switchMap, tap } from 'rxjs';

import { EntLoaderService } from '@ng/ent-components/ent-loader';

import { AgentComment, BRERequirement, ContractData, NIGOComment, NIGOComments, ResponseStatusInterface } from 'src/app/shared/models/app.model';
import { AgentService } from 'src/app/shared/services/agent.service';
import { AnalyticsAppService } from 'src/app/shared/services/analytics.service';
import { ContractDetailsService } from 'src/app/shared/services/contract-details.service';
import { DownloadFileService } from 'src/app/shared/services/download-file.service';
import { UtilsService } from 'src/app/shared/services/utils.service';
import { Message, ResponseStatus } from 'src/config/constants';
import { FileMetadataService } from 'src/app/shared/services/file-metadata.service';
import { EntFormWidgetsModule } from '@ng/ent-components/ent-form-widgets';
import { OutstandingRequirementFormComponent } from '../outstanding-requirement-form/outstanding-requirement-form.component';

@Component({
    selector: 'app-outstanding-requirements',
    templateUrl: './outstanding-requirements.component.html',
    styleUrls: ['./outstanding-requirements.component.scss'],
    imports: [EntFormWidgetsModule, OutstandingRequirementFormComponent, AsyncPipe],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class OutstandingRequirementsComponent implements OnInit {
  private readonly contractDetailsService = inject(ContractDetailsService);
  private readonly utilService = inject(UtilsService);
  private readonly location = inject(Location);
  private readonly agentService = inject(AgentService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly analytics = inject(AnalyticsAppService);
  private readonly downloadFileService = inject(DownloadFileService);
  private readonly entLoaderService = inject(EntLoaderService);
  private readonly fileMetadataService = inject(FileMetadataService);
  private readonly cdr = inject(ChangeDetectorRef);


  contractNumber!: number;
  ownerName: string | undefined;
  outstandingRequirementsFYIInfo = Message.outstandingRequirementsFYInfo;
  contractData!: ContractData;
  BRERequirements!: BRERequirement[];
  NIGOComments!: NIGOComment[];
  AgentComments!: AgentComment[];
  isBRERequirements = false;
  displayFYI = false;
  allRequirementsResolved = false;
  responseStatus: ResponseStatusInterface = ResponseStatus;
  apiStatus$: Observable<string> | undefined;
  message = Message;
  setfocusonHeader = true;
  fileMetadata: any;

  ngOnInit(): void {
    window.scroll(0, 0);
    const contractNumberParam = this.activatedRoute.snapshot.params['contractNumber'];
    this.contractNumber = contractNumberParam ? Number(contractNumberParam) : 0;
    this.contractDetailsService.setContractNumber(this.contractNumber);
    this.contractData = this.contractDetailsService.getContractDetails();
    if (this.contractData) {
      if (location.href.includes('#')) {
        this.setFocus(location.href?.split('#')[2] + '-header');
        this.setfocusonHeader = !location.href?.split('#')[2];
        this.location.replaceState(location.href?.split('#')[1]);
      }
      this.apiStatus$ = of(this.responseStatus.success);
      this.loadPageData();
    } else {
      this.fetchContractDetails();
    }
    this.fileMetadata = this.fileMetadataService.getFileMetadata();
    if (!this.fileMetadata) {
      this.fetchFileMetadata();
    }
  }

  /**
   * fetch contract details again if data is not available due to page reload
   * @returns {void}
   */
  fetchContractDetails(): void {
    this.apiStatus$ = this.agentService.agentProfileSubject.pipe(
      tap((agentResponse: any) => agentResponse ),
      switchMap((agentResponse: any) => agentResponse ? this.contractDetailsService.fetchContractDetails(agentResponse): ''),
      tap((contractRespone: any) => {
        if (contractRespone !== this.responseStatus.success) {
          return;
        }
        this.contractData = this.contractDetailsService.getContractDetails();
        const clientName = (this.contractData?.ClientFirstName || this.contractData?.ClientLastName)
        ? `${this.contractData?.ClientLastName}, ${this.contractData?.ClientFirstName}` : '';
        this.utilService.setOwnerName(clientName);
        this.utilService.setBRERequirements(this.contractData.BRERequirement);
        this.utilService.setNIGOComments(this.contractData.NIGOComment);
        this.utilService.setAgentComments(this.contractData.AgentComment);
        this.agentService.setHantzReadOnly(this.contractData.HantzReadOnly);
        this.loadPageData();
      }));
    this.agentService.fetchAgentProfile();
  }

  /**
   * Loads data from the utilService service and assigns it to the component properties.
   * @returns {void}
   */
  loadPageData(): void {
    this.contractNumber = this.contractDetailsService.getContractNumber();
    this.ownerName = this.utilService.getOwnerName();
    this.BRERequirements = this.utilService.getBRERequirements()?.filter((data) => { return data.RequirementSeverity === 'NIGO'; });
    this.isBRERequirements = this.BRERequirements?.length > 0;
    this.NIGOComments = this.utilService.getNIGOComments();
    this.AgentComments = this.utilService.getAgentComments();
    this.displayConditionForFYISection();
    this.allOutstandingRequirementsResolved();
    this.getOutstandingCommentsTableData();
    if (this.setfocusonHeader) {
      this.setFocus('pageheader');
    }
    this.analytics.logPageView('outstanding requirements');
  }

  /**
   * Adds tabindex and sets focus dynamically to the header elements for ADA
   * @returns {void}
   */
  setFocus(elementId: string): void {
    setTimeout(() => {
      const element = document.getElementById(elementId);
      if (!element) {
        return;
      }
      element.setAttribute('tabindex', '0');
      element.focus();
      setTimeout(() => {
        element.setAttribute('tabindex', '-1');
      }, 3000);
    }, 200);
  }

  /**
   * Checks display condition for FYI section
   * @returns {void}
   */
  displayConditionForFYISection(): void {
    this.displayFYI = this.contractData?.BRERequirement?.filter((data) => {
      return data.RequirementSeverity === 'INFO' && data.RequirementStatus !== 'Complete';
    }).length > 0;
  }

  /**
   * navigate back to Annuity Pending Details
   * @returns {void}
   */
  backToAnnuityPendingDetails(): void {
    this.router.navigateByUrl(`/contractdetails?contractNumber=${this.contractNumber}`);
    this.analytics.logAnalytics({
      event_action: 'link',
      event_name: 'back to annuity pending details',
      event_type: 'pending tool - annuity'
    })
  }

  /**
   * get data for outstanding requirment comments table from NIGOComment and AgentComment objects
   * @returns {void}
   */
  getOutstandingCommentsTableData(): void {
    this.BRERequirements?.map((BRERdata: BRERequirement, index) => {
      const NIGOandAgentCommentObj = this.getNIGOandAgentComments(BRERdata.ID);
      this.BRERequirements[index].TableData = NIGOandAgentCommentObj;
    });
  }

  /**
   * filter and map NIGOComment and AgentComment based on BRERequirements ID
   * @param BRERequirements ID
   * @returns formatted array
   */
  getNIGOandAgentComments(BRERdataId: string): Array<any> {
    return (this.NIGOComments?.filter((NIGOdata: NIGOComments) => {
      return NIGOdata.ID === BRERdataId;
    })?.map((NIGOval: NIGOComments) => {
      return {
        dateandtime: NIGOval?.NIGOCommentDate ? this.utilService.getFormattedDate(NIGOval.NIGOCommentDate) : '',
        user: 'Lincoln Financial',
        comment: NIGOval.NIGOCommentText,
      }
    }) ?? []).concat(
     this.AgentComments?.filter((agentData: AgentComment) => {
      return  agentData.ID === BRERdataId;
    })?.map((agentVal: AgentComment) => {
      return {
        dateandtime: agentVal?.AgentCommentDate ? this.utilService.getFormattedDate(agentVal.AgentCommentDate) : '',
        user: agentVal.AgentName,
        comment: agentVal.AgentCommentText
      }
    }) ?? []);
  }

  /**
   * Checks all outstanding requirements completed or not and update the header accordingly for Action Needed section
   * @returns {void}
   */
  allOutstandingRequirementsResolved(): void {
    this.allRequirementsResolved = this.contractData?.BRERequirement?.filter((data) => {
      return data.RequirementSeverity === 'NIGO' && data.RequirementStatus !== 'Complete';
    }).length > 0;
  }

  /**
   * Fetches the metadata for files to download
   * @returns {void}
   */
  fetchFileMetadata(): void {
    this.fileMetadataService.fetchFileMetadata().subscribe((res)=>{
      if (res == this.responseStatus.success){
        this.fileMetadata = this.fileMetadataService.getFileMetadata();
      }
      this.cdr.markForCheck();
    })
  }

}
